import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notify from '@/services/notifications';

const ANALYTICS_PERIOD_STORAGE_KEY = 'admin.analytics.period.v1';
const DEFAULT_ANALYTICS_PERIOD = {
    range: 'last_30_days',
    customStart: null,
    customEnd: null,
    shift: 0,
};

const AdminContext = createContext({
    content: null,
    loading: true,
    authorized: false,
    // Who is signed in: the site owner (role 'admin') or a content editor
    actor: null,
    refetch: () => {},
    routebase: '/admin',
    // Client identity
    clientId: null,
    // Connection status
    isConnected: false,
    connectionStatus: 'disconnected',
    reconnectWebSocket: () => {},
    // Editing scope tracking
    editingScope: null,
    setEditingScope: () => {},
    // Collaborative editing
    activeEditors: {},
    liveUpdates: {},
    requestLock: () => {},
    releaseLock: () => {},
    claimLock: () => {},
    broadcastLiveUpdate: () => {},
    lockKicked: null,
    clearLockKicked: () => {},
    // Generic message sending (for upload notifications, etc.)
    sendMessage: () => {},
    // Remote upload state (when other users are uploading)
    remoteUploadState: { isUploading: false, uploaderName: null, totalFiles: 0 },
    // Global analytics period preferences
    analyticsPeriod: DEFAULT_ANALYTICS_PERIOD.range,
    setAnalyticsPeriod: () => {},
    analyticsCustomStart: DEFAULT_ANALYTICS_PERIOD.customStart,
    analyticsCustomEnd: DEFAULT_ANALYTICS_PERIOD.customEnd,
    analyticsPeriodShift: DEFAULT_ANALYTICS_PERIOD.shift,
    setAnalyticsCustomStart: () => {},
    setAnalyticsCustomEnd: () => {},
    setAnalyticsPeriodShift: () => {},
});

export const useAdminContext = () => useContext(AdminContext);

/**
 * Convert editing scope to entity key for locking. Inside a version
 * (`scope.releaseId`) pages and home lock as `v:<releaseId>:page:<id>` and
 * `v:<releaseId>:home`, apart from Live and from other versions.
 */
export function scopeToEntityKey(scope) {
    if (!scope) return null;
    const prefix = scope.releaseId ? `v:${scope.releaseId}:` : '';
    if (scope.type === 'page' && scope.id) return `${prefix}page:${scope.id}`;
    if (scope.type === 'home') return `${prefix}home`;
    if (scope.type === 'settings') return 'settings';
    return null;
}

/**
 * Admin Context Provider
 * Fetches admin data via API (client-side only)
 * Supports collaborative editing with locks and live updates
 */
export function AdminProvider({ children }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState(false);
    const [actor, setActor] = useState(null);
    const actorIdRef = useRef(null);
    
    // Client identity (assigned by server)
    const [clientId, setClientId] = useState(null);
    
    // Connection status
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [wsReconnectToken, setWsReconnectToken] = useState(0);
    
    // Track what the user is currently editing
    const [editingScope, setEditingScopeInternal] = useState(null);
    
    // Active editors across all entities
    const [activeEditors, setActiveEditors] = useState({});
    
    // Live updates from other editors (entityKey -> liveState)
    const [liveUpdates, setLiveUpdates] = useState({});
    
    // Track if we were kicked from editing
    const [lockKicked, setLockKicked] = useState(null);
    
    // Track remote upload state (when other users are uploading)
    const [remoteUploadState, setRemoteUploadState] = useState({
        isUploading: false,
        uploaderName: null,
        totalFiles: 0,
    });
    
    // Global analytics period state (persisted in localStorage)
    const [analyticsPeriod, setAnalyticsPeriod] = useState(DEFAULT_ANALYTICS_PERIOD.range);
    const [analyticsCustomStart, setAnalyticsCustomStart] = useState(DEFAULT_ANALYTICS_PERIOD.customStart);
    const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState(DEFAULT_ANALYTICS_PERIOD.customEnd);
    const [analyticsPeriodShift, setAnalyticsPeriodShift] = useState(DEFAULT_ANALYTICS_PERIOD.shift);
    
    // WebSocket ref for sending messages
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectEnabledRef = useRef(true);
    
    // Refs for latest state
    const editingScopeRef = useRef(editingScope);
    const clientIdRef = useRef(clientId);
    
    useEffect(() => { editingScopeRef.current = editingScope; }, [editingScope]);
    useEffect(() => { clientIdRef.current = clientId; }, [clientId]);
    
    // Hydrate analytics period preferences from localStorage.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const raw = window.localStorage.getItem(ANALYTICS_PERIOD_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (typeof parsed?.range === 'string') {
                setAnalyticsPeriod(parsed.range);
            }
            if (typeof parsed?.customStart === 'string' || parsed?.customStart === null) {
                setAnalyticsCustomStart(parsed.customStart);
            }
            if (typeof parsed?.customEnd === 'string' || parsed?.customEnd === null) {
                setAnalyticsCustomEnd(parsed.customEnd);
            }
        } catch {
            // Ignore persistence parse errors.
        }
        // Period shift is intentionally transient; always start from current window.
        setAnalyticsPeriodShift(0);
    }, []);
    
    // Persist analytics period preferences.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(
                ANALYTICS_PERIOD_STORAGE_KEY,
                JSON.stringify({
                    range: analyticsPeriod,
                    customStart: analyticsCustomStart,
                    customEnd: analyticsCustomEnd,
                }),
            );
        } catch {
            // Ignore persistence write errors.
        }
    }, [analyticsPeriod, analyticsCustomStart, analyticsCustomEnd]);

    // Send message via WebSocket
    const sendMessage = useCallback((msg) => {
        if (wsRef.current && wsRef.current.readyState === 1) {
            wsRef.current.send(JSON.stringify(msg));
        }
    }, []);

    const clearReconnectTimeout = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    const scheduleReconnect = useCallback((delayMs = 1200) => {
        if (reconnectTimeoutRef.current || !reconnectEnabledRef.current) {
            return;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (!reconnectEnabledRef.current) return;
            setConnectionStatus('reconnecting');
            setWsReconnectToken((prev) => prev + 1);
        }, delayMs);
    }, []);

    // Manual reconnect (used by connection warning toast)
    const reconnectWebSocket = useCallback(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            return;
        }
        clearReconnectTimeout();
        setConnectionStatus('reconnecting');
        setWsReconnectToken((prev) => prev + 1);
    }, [clearReconnectTimeout]);
    
    // Request a lock on an entity
    const requestLock = useCallback((entityKey) => {
        sendMessage({ type: 'lock', entityKey });
    }, [sendMessage]);
    
    // Release a lock on an entity
    const releaseLock = useCallback((entityKey) => {
        sendMessage({ type: 'unlock', entityKey });
    }, [sendMessage]);
    
    // Claim a lock from someone else
    const claimLock = useCallback((entityKey) => {
        sendMessage({ type: 'claim', entityKey });
    }, [sendMessage]);
    
    // Broadcast live update to others
    const broadcastLiveUpdate = useCallback((entityKey, liveState) => {
        sendMessage({ type: 'live_update', entityKey, liveState });
    }, [sendMessage]);
    
    // Clear lock kicked state
    const clearLockKicked = useCallback(() => {
        setLockKicked(null);
    }, []);

    // Custom setEditingScope that also handles locks
    const setEditingScope = useCallback((scope) => {
        const oldScope = editingScopeRef.current;
        const oldKey = scopeToEntityKey(oldScope);
        const newKey = scopeToEntityKey(scope);
        
        // Release old lock if different
        if (oldKey && oldKey !== newKey) {
            releaseLock(oldKey);
        }
        
        // Request new lock if needed
        if (newKey && newKey !== oldKey) {
            requestLock(newKey);
        }
        
        setEditingScopeInternal(scope);
    }, [requestLock, releaseLock]);

    const fetchContent = useCallback(async () => {
        try {
            const authRes = await fetch('/api/admin/auth');
            if (!authRes.ok) {
                throw new Error(`Auth check failed: ${authRes.status}`);
            }
            const authData = await authRes.json();
            
            if (!authData.authorized) {
                setAuthorized(false);
                setActor(null);
                setLoading(false);
                return;
            }

            // Someone else signed in over the blurred workspace: start afresh
            // rather than show them what the last person had open.
            const nextId = authData.actor?.id || null;
            if (actorIdRef.current && nextId && actorIdRef.current !== nextId) {
                window.location.assign('/admin');
                return;
            }
            actorIdRef.current = nextId;
            setActor(authData.actor || null);
            setAuthorized(true);

            const contentRes = await fetch('/api/admin', {
                credentials: 'include',
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache' },
            });
            if (!contentRes.ok) {
                throw new Error(`Content fetch failed: ${contentRes.status}`);
            }
            const contentData = await contentRes.json();
            setContent(contentData);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch admin data:', err);
            setAuthorized(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContent();
    }, [fetchContent]);

    // Validate login when window regains focus or becomes visible
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const checkAuth = () => {
            // Always check auth on focus/visibility change to catch session expiration
            console.log('[AdminContext] Checking auth on focus/visibility change');
            fetch('/api/admin/auth', {
                credentials: 'include',
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                },
            })
                .then(res => res.json())
                .then(data => {
                    if (!data.authorized && authorized) {
                        // Session expired while we thought we were authorized
                        console.log('[AdminContext] Session expired - showing login overlay');
                        setAuthorized(false);
                    } else if (data.authorized && authorized && data.actor) {
                        setActor(data.actor);
                    } else if (data.authorized && !authorized) {
                        // Session restored (user logged in via overlay)
                        console.log('[AdminContext] Session restored - refreshing content');
                        setAuthorized(true);
                        // Refresh content if we're now authorized
                        fetchContent();
                    }
                })
                .catch(err => {
                    console.error('[AdminContext] Auth check on focus/visibility failed:', err);
                });
        };

        // Use visibilitychange for better reliability (works when switching tabs)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // Page became visible - check auth
                checkAuth();
                if (authorized && !isConnected) {
                    reconnectWebSocket();
                }
            }
        };

        // Also listen to focus as backup
        const handleFocus = () => {
            checkAuth();
            if (authorized && !isConnected) {
                reconnectWebSocket();
            }
        };

        const handleOnline = () => {
            if (authorized && !isConnected) {
                reconnectWebSocket();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
        };
    }, [authorized, fetchContent, isConnected, reconnectWebSocket]);
    
    // Handle content updates (from saved DB changes)
    // Always accept remote changes - conflict detection is disabled since we use 
    // real-time locking instead. This ensures uploads and replacements update immediately.
    const handleContentUpdate = useCallback((remoteContent) => {
        setContent(remoteContent);
    }, []);

    // Setup WebSocket
    useEffect(() => {
        if (!authorized || typeof window === 'undefined') return;
        reconnectEnabledRef.current = true;
        clearReconnectTimeout();

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/api/admin`;
        console.log('[AdminContext] Connecting WebSocket to:', wsUrl);
        setConnectionStatus((prev) => (prev === 'connected' ? 'connected' : 'reconnecting'));
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log('[AdminContext] WebSocket connected');
            setIsConnected(true);
            setConnectionStatus('connected');
            clearReconnectTimeout();
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                
                switch (msg.type) {
                    case 'init':
                        // Initial connection - set client ID and content
                        console.log('[AdminContext] Initialized with clientId:', msg.clientId);
                        setClientId(msg.clientId);
                        setContent(msg.content);
                        setActiveEditors(msg.editors || {});
                        setLoading(false);
                        break;
                        
                    case 'content_update':
                        // Database content changed (someone saved)
                        console.log('[AdminContext] Content update received');
                        handleContentUpdate(msg.content);
                        break;
                        
                    case 'editors_update':
                        // Active editors changed (someone started/stopped editing)
                        console.log('[AdminContext] Editors update:', msg.editors);
                        setActiveEditors(msg.editors || {});
                        break;
                        
                    case 'live_update':
                        // Live edit from another user
                        setLiveUpdates(prev => ({
                            ...prev,
                            [msg.entityKey]: {
                                liveState: msg.liveState,
                                editor: msg.editor,
                                timestamp: Date.now(),
                            }
                        }));
                        break;
                        
                    case 'lock_denied':
                        // Our lock request was denied
                        console.log('[AdminContext] Lock denied for', msg.entityKey, '- locked by', msg.lockedBy?.name);
                        // The UI should show this is locked by someone else
                        if (msg.reason === 'owner_editing') {
                            notify.refusal(`${msg.lockedBy?.name || 'The site owner'} is editing this right now. You can take over once they’re done.`);
                        }
                        break;
                        
                    case 'lock_claimed': {
                        // Someone took over our editing lock
                        console.log('[AdminContext] Lock claimed by', msg.claimedBy?.name, 'for', msg.entityKey);
                        setLockKicked({
                            entityKey: msg.entityKey,
                            claimedBy: msg.claimedBy,
                        });
                        // Clear our editing scope for this entity
                        const currentKey = scopeToEntityKey(editingScopeRef.current);
                        if (currentKey === msg.entityKey) {
                            setEditingScopeInternal(null);
                        }
                        break;
                    }
                    
                    case 'releases_update':
                        // Versions changed (here, in another tab, or by someone else).
                        // useReleases adopts the list; the editor warns when the
                        // version it has open changed under unsaved edits.
                        window.dispatchEvent(new CustomEvent('portfolio:releases-update', {
                            detail: {
                                releases: msg.releases,
                                liveId: msg.liveId,
                                changedIds: msg.changedIds || [],
                                by: msg.by || null,
                            },
                        }));
                        break;

                    case 'actor_update':
                        // The owner changed this editor's access.
                        setActor(msg.actor || null);
                        break;

                    case 'signed_out':
                        // Turned off, removed, or signed out everywhere by the owner.
                        reconnectEnabledRef.current = false;
                        setAuthorized(false);
                        setActor(null);
                        break;

                    case 'upload-start':
                        // Another user started uploading files
                        setRemoteUploadState({
                            isUploading: true,
                            uploaderName: msg.uploaderName || 'Another user',
                            totalFiles: msg.totalFiles || 0,
                        });
                        break;
                        
                    case 'upload-end':
                        // Another user finished uploading files
                        setRemoteUploadState({
                            isUploading: false,
                            uploaderName: null,
                            totalFiles: 0,
                        });
                        break;
                        
                    default:
                        // Legacy: plain content object (backwards compatibility)
                        if (msg.pages || msg.files || msg.home) {
                            console.log('[AdminContext] Legacy content update');
                            handleContentUpdate(msg);
                        }
                }
            } catch (err) {
                console.error('[AdminContext] WebSocket parse error:', err);
            }
        };

        ws.onerror = (err) => {
            console.error('[AdminContext] WebSocket error:', err);
            setIsConnected(false);
            if (reconnectEnabledRef.current) {
                setConnectionStatus('disconnected');
            }
        };
        
        ws.onclose = (event) => {
            console.log('[AdminContext] WebSocket closed:', event.code, event.reason);
            setIsConnected(false);
            wsRef.current = null;
            if (reconnectEnabledRef.current) {
                setConnectionStatus('disconnected');
                scheduleReconnect();
            }
        };

        return () => {
            console.log('[AdminContext] Closing WebSocket');
            reconnectEnabledRef.current = false;
            clearReconnectTimeout();
            // Release any locks we hold
            const currentKey = scopeToEntityKey(editingScopeRef.current);
            if (currentKey && ws.readyState === 1) {
                ws.send(JSON.stringify({ type: 'unlock', entityKey: currentKey }));
            }
            ws.close();
            setIsConnected(false);
            wsRef.current = null;
            setConnectionStatus('disconnected');
        };
    }, [authorized, handleContentUpdate, wsReconnectToken, scheduleReconnect, clearReconnectTimeout]);

    const value = {
        content,
        loading,
        authorized,
        actor,
        refetch: fetchContent,
        routebase: '/admin',
        // Client identity
        clientId,
        // Connection status
        isConnected,
        connectionStatus,
        reconnectWebSocket,
        // Editing scope tracking
        editingScope,
        setEditingScope,
        // Collaborative editing
        activeEditors,
        liveUpdates,
        requestLock,
        releaseLock,
        claimLock,
        broadcastLiveUpdate,
        lockKicked,
        clearLockKicked,
        // Generic message sending (for upload notifications, etc.)
        sendMessage,
        // Remote upload state (when other users are uploading)
        remoteUploadState,
        // Global analytics period preferences
        analyticsPeriod,
        setAnalyticsPeriod,
        analyticsCustomStart,
        analyticsCustomEnd,
        analyticsPeriodShift,
        setAnalyticsCustomStart,
        setAnalyticsCustomEnd,
        setAnalyticsPeriodShift,
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
}

export { AdminContext };
export default AdminProvider;
